export type InferredCarrier = 'UPS' | 'USPS' | 'FedEx' | 'ZoomLogi Simulator';

/**
 * Infer carrier from a pasted transport tracking number using ordered heuristics.
 * Returns null when no rule matches.
 */
export function inferCarrierFromTrackingId(
  raw: string
): InferredCarrier | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }

  // 0. ZoomLogi Simulator — synthetic courier ids start with SIM-
  if (/^sim-/i.test(trimmed)) {
    return 'ZoomLogi Simulator';
  }

  // 1. UPS — starts with 1Z (case-insensitive) after trim; any length
  if (/^1z/i.test(trimmed)) {
    return 'UPS';
  }

  // 2. Starts with "420" — USPS only if all digits AND length >= 26; else no pre-fill (do not fall through)
  if (trimmed.startsWith('420')) {
    const digitsOnly = trimmed.replace(/\s/g, '');
    if (/^\d+$/.test(digitsOnly) && digitsOnly.length >= 26) {
      return 'USPS';
    }
    return null;
  }

  const digitsOnly = trimmed.replace(/\s/g, '');
  const isAllDigits = /^\d+$/.test(digitsOnly);
  const len = digitsOnly.length;

  // 3. All digits, length 20–22, starts with 92–95 → USPS
  if (isAllDigits && len >= 20 && len <= 22) {
    if (
      digitsOnly.startsWith('94') ||
      digitsOnly.startsWith('93') ||
      digitsOnly.startsWith('92') ||
      digitsOnly.startsWith('95')
    ) {
      return 'USPS';
    }
  }

  // 4. All digits, length 12 or 15 → FedEx
  if (isAllDigits && (len === 12 || len === 15)) {
    return 'FedEx';
  }

  // 5. All digits, length 13 → USPS
  if (isAllDigits && len === 13) {
    return 'USPS';
  }

  return null;
}
